import { useCallback, useMemo } from 'react'
import useCollapse from 'react-collapsed'
import { FormattedMessage, useIntl } from 'react-intl'
import {
  CardContent,
  CardHeader,
  CardInfo,
  CardTitle,
  createColumns,
  csx,
  IconCaretDown,
  IconCaretRight,
  Table,
  TBody,
  TBodyCell,
  TBodyRow,
  THead,
  THeadCell,
  useTableState,
} from '@vtex/admin-ui'
import { useAdmin } from '@vtex/raccoon-next'
import { ImageWithFallback } from '@/components/ImageWithFallback'
import { getValidateCurrencyCode } from '@/lib/currency'
import { extractSKUNameFromNameComplete } from '@/lib/transform'
import type { Product, Seller, SKUListing } from '@/types/product'

import * as styles from './SKUOffersTable.styles'

interface SkuOffersTableProps {
  sku: SKUListing
  product: Product
  defaultExpanded?: boolean
}

export const SkuOffersTable = (props: SkuOffersTableProps) => {
  const { sku, product, defaultExpanded = false } = props
  const { locale = 'en-US', currency } = useAdmin()
  const { formatMessage } = useIntl()

  
  const validCurrency = getValidateCurrencyCode(currency)

  const columns = useMemo(
    () =>
      createColumns<Seller>([
        {
          id: 'name',
          header: formatMessage({
            id: 'admin/admin.catalog.sellers-table-name',
            defaultMessage: 'Seller',
          }),
          width: '1fr',
          resolver: {
            type: 'plain',
            render: SellerName,
          },
        },
        {
          id: 'saleChannel',
          header: formatMessage({
            id: 'admin/admin.catalog.sellers-table-sale-channel',
            defaultMessage: 'Sales channel',
          }),
          resolver: {
            type: 'plain',
            render: SaleChannel,
          },
        },
        {
          id: 'price',
          header: formatMessage({
            id: 'admin/admin.catalog.sellers-table-price',
            defaultMessage: 'Price',
          }),
          resolver: { type: 'currency', locale, currency: validCurrency },
        },
        {
          id: 'inventory',
          header: formatMessage({
            id: 'admin/admin.catalog.sellers-table-inventory',
            defaultMessage: 'Inventory',
          }),
          resolver: {
            type: 'plain',
            render: Inventory,
          },
        },
      ]),
    [validCurrency, formatMessage, locale]
  )

  const { getCollapseProps, getToggleProps, isExpanded } = useCollapse({
    defaultExpanded,
  })

  const emptyData: Seller[] = [
    {
      id: '',
      name: '',
      saleChannel: 0,
      price: 0,
      inventory: 0,
    },
  ]

  const { getTable, getBodyCell, getHeadCell, data } = useTableState({
    items: sku.sellers,
    columns,
  })

  const skuName = extractSKUNameFromNameComplete(sku.nameComplete, product.name)
  const label = `${sku.id} - ${skuName}`

  const skuOfferWrapperCallback = useCallback(
    (element: HTMLDivElement) => {
      if (element && defaultExpanded) {
        element?.scrollIntoView?.()
      }
    },
    [defaultExpanded]
  )

  return (
    <div ref={skuOfferWrapperCallback} className={csx(styles.skuOffersWrapper)}>
      <CardHeader className={csx(styles.cardHeader)} {...getToggleProps()}>
        <CardInfo>
          {isExpanded ? (
            <IconCaretDown aria-label="Expanded card icon" />
          ) : (
            <IconCaretRight aria-label="Collapsed card icon" />
          )}
          <ImageWithFallback
            src={sku.mainImageUrl}
            alt={sku.nameComplete}
            width={32}
            height={32}
          />
          <CardTitle>{label}</CardTitle>
        </CardInfo>
      </CardHeader>
      <section {...getCollapseProps()}>
        <CardContent
          className={csx({
            padding: '$space-6 $space-7',
            paddingTop: '$space-2',
          })}
        >
          {isExpanded && (
            <Table {...getTable()}>
              <THead>
                {columns.map((column) => (
                  <THeadCell {...getHeadCell(column)} key={column.id} />
                ))}
              </THead>
              <TBody className={csx(styles.tableBody)}>
                {data.length < 1 && (
                  <TBodyRow>
                    {columns.map((column) => (
                      <TBodyCell
                        {...getBodyCell(column, emptyData[0])}
                        key={column.id}
                      />
                    ))}
                  </TBodyRow>
                )}
                {data.map((item) => (
                  <TBodyRow key={item.id}>
                    {columns.map((column) => (
                      <TBodyCell
                        {...getBodyCell(column, item)}
                        key={column.id}
                      />
                    ))}
                  </TBodyRow>
                ))}
              </TBody>
            </Table>
          )}
        </CardContent>
      </section>
    </div>
  )
}

type SellerNameProps = {
  item: Seller
}

const SellerName = ({ item }: SellerNameProps) => (
  <span className={csx({ marginRight: 'auto' })}>
    {item.name || item.id || (
      <FormattedMessage
        id="admin/admin.catalog.sellers-table-no-name"
        defaultMessage="None"
      />
    )}
  </span>
)

const SaleChannel = ({ item }: SellerNameProps) => (
  <span className={csx({ marginRight: 'auto' })}>
    {Array.isArray(item.saleChannel)
      ? item.saleChannel.join?.(', ')
      : item.saleChannel || '-'}
  </span>
)

const Inventory = ({ item }: SellerNameProps) => (
  <span className={csx({ marginRight: 'auto' })}>{item.inventory || '-'}</span>
)
